import { CommonModule } from "@angular/common";
import { Component } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { AiFeedbackService } from "../../services/ai-feedback.service";

/**
 * Shared, globally-mounted reason dialog for AI feedback (EU AI Act / ISO 42001).
 * Opened by AiFeedbackService.open() from any 👍/👎 control. Collects an optional
 * reason and submits rating + reason to ai/feedback.
 */
@Component({
  selector: "app-ai-feedback-dialog",
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="ai-fb-overlay" *ngIf="feedback.active$ | async as a">
      <div class="ai-fb-dialog">
        <div class="ai-fb-dialog__head">
          <span
            class="ai-fb-dialog__icon"
            [class.up]="a.rating === 'up'"
            [class.down]="a.rating === 'down'">
            <i [class]="a.rating === 'up' ? 'pi pi-thumbs-up' : 'pi pi-thumbs-down'"></i>
          </span>
          <h3 class="ai-fb-dialog__title">
            {{ a.rating === "up" ? "Glad it was helpful!" : "Sorry it wasn't helpful." }}
          </h3>
        </div>
        <p class="ai-fb-dialog__sub">
          Tell us why (optional) — your feedback helps improve the AI.
        </p>
        <textarea
          class="ai-fb-dialog__input"
          rows="4"
          [(ngModel)]="reason"
          placeholder="Add a reason…"></textarea>
        <div class="ai-fb-dialog__actions">
          <button type="button" class="ghost" (click)="onCancel()">Cancel</button>
          <button type="button" class="primary" (click)="onSubmit()">
            Submit feedback
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      .ai-fb-overlay {
        position: fixed;
        inset: 0;
        z-index: 7000;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(2, 6, 23, 0.55);
        padding: 16px;
      }
      .ai-fb-dialog {
        width: 100%;
        max-width: 440px;
        background: #fff;
        border-radius: 14px;
        box-shadow: 0 20px 60px rgba(2, 6, 23, 0.35);
        padding: 22px 24px 20px;
        color: #1f2937;
      }
      .ai-fb-dialog__head {
        display: flex;
        align-items: center;
        gap: 12px;
      }
      .ai-fb-dialog__icon {
        width: 34px;
        height: 34px;
        border-radius: 50%;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 15px;
      }
      .ai-fb-dialog__icon.up {
        background: #dcfce7;
        color: #16a34a;
      }
      .ai-fb-dialog__icon.down {
        background: #fef9c3;
        color: #ca8a04;
      }
      .ai-fb-dialog__title {
        font-size: 17px;
        font-weight: 700;
        color: #0f172a;
        margin: 0;
      }
      .ai-fb-dialog__sub {
        margin: 12px 0 8px;
        font-size: 13px;
        color: #64748b;
      }
      .ai-fb-dialog__input {
        width: 100%;
        border: 1px solid #d1d5db;
        border-radius: 8px;
        padding: 10px 12px;
        font-size: 13px;
        color: #1f2937;
        resize: vertical;
        box-sizing: border-box;
      }
      .ai-fb-dialog__input:focus {
        outline: none;
        border-color: #2563eb;
        box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
      }
      .ai-fb-dialog__actions {
        display: flex;
        justify-content: flex-end;
        gap: 10px;
        margin-top: 16px;
      }
      .ai-fb-dialog__actions .ghost {
        background: #fff;
        border: 1px solid #cbd5e1;
        color: #475569;
        padding: 8px 16px;
        border-radius: 8px;
        cursor: pointer;
        font-size: 13px;
        font-weight: 600;
      }
      .ai-fb-dialog__actions .primary {
        background: #2563eb;
        border: 1px solid #2563eb;
        color: #fff;
        padding: 8px 18px;
        border-radius: 8px;
        cursor: pointer;
        font-size: 13px;
        font-weight: 600;
      }
      .ai-fb-dialog__actions .primary:hover {
        background: #1d4ed8;
      }
    `,
  ],
})
export class AiFeedbackDialogComponent {
  reason = "";

  constructor(public feedback: AiFeedbackService) {}

  onSubmit(): void {
    this.feedback.submit(this.reason);
    this.reason = "";
  }

  onCancel(): void {
    this.feedback.cancel();
    this.reason = "";
  }
}
